// Camera selection. On iPhone, Safari exposes the physical wide camera as
// "Back Camera" alongside virtual multi-camera devices that switch lenses on
// their own; a fixed lens keeps calibration valid, so we prefer the physical one.

export interface CameraChoice {
  deviceId: string;
  label: string;
}

export interface OpenedCamera {
  stream: MediaStream;
  deviceId: string | null;
  label: string | null;
}

const VIRTUAL_OR_OTHER = /(dual|triple|ultra|tele|front|desk|continuity)/i;

export async function listCameras(): Promise<CameraChoice[]> {
  const devices = await navigator.mediaDevices.enumerateDevices();
  return devices.filter((d) => d.kind === 'videoinput').map((d) => ({ deviceId: d.deviceId, label: d.label || 'Camera' }));
}

export function preferredBackCamera(list: CameraChoice[]): CameraChoice | null {
  return (
    list.find((c) => /^back camera$/i.test(c.label.trim())) ??
    list.find((c) => /back|rear|environment/i.test(c.label) && !VIRTUAL_OR_OTHER.test(c.label)) ??
    null
  );
}

export function cameraSupported(): boolean {
  return !!navigator.mediaDevices?.getUserMedia;
}

export async function openCamera(opts: { deviceId: string | null; resolution: 720 | 1080; fps: 30 | 60 }): Promise<OpenedCamera> {
  if (!cameraSupported()) throw new Error('This browser can’t access the camera. Open the app over HTTPS in Safari or Chrome.');
  const size = opts.resolution === 1080 ? { width: 1920, height: 1080 } : { width: 1280, height: 720 };
  const video = (deviceId: string | null): MediaTrackConstraints => ({
    ...(deviceId ? { deviceId: { exact: deviceId } } : { facingMode: { ideal: 'environment' } }),
    width: { ideal: size.width },
    height: { ideal: size.height },
    frameRate: { ideal: opts.fps },
  });
  let stream: MediaStream;
  try {
    stream = await navigator.mediaDevices.getUserMedia({ video: video(opts.deviceId), audio: false });
  } catch (e) {
    if (opts.deviceId && (e as DOMException)?.name === 'OverconstrainedError') {
      stream = await navigator.mediaDevices.getUserMedia({ video: video(null), audio: false });
    } else {
      throw friendlyCameraError(e);
    }
  }
  // Labels are only available after permission; switch to the physical wide camera if we can.
  if (!opts.deviceId) {
    const best = preferredBackCamera(await listCameras().catch(() => []));
    const current = stream.getVideoTracks()[0];
    if (best && current && current.getSettings().deviceId !== best.deviceId) {
      current.stop();
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: video(best.deviceId), audio: false });
      } catch {
        stream = await navigator.mediaDevices.getUserMedia({ video: video(null), audio: false });
      }
    }
  }
  const track = stream.getVideoTracks()[0];
  return { stream, deviceId: track?.getSettings().deviceId ?? null, label: track?.label ?? null };
}

function friendlyCameraError(e: unknown): Error {
  const name = (e as DOMException)?.name;
  if (name === 'NotAllowedError') return new Error('Camera access was blocked. Allow camera access for this site in your browser settings, then try again.');
  if (name === 'NotFoundError') return new Error('No camera was found on this device.');
  if (name === 'NotReadableError') return new Error('The camera is in use by another app.');
  return e instanceof Error ? e : new Error(String(e));
}
